import React from 'react';
import { useAct, gsap, DEBUG, prefersReduced } from '../hooks/useScrollStory';

/* ══════════════════════════════════════════════════════
   GÖRSEL DUVARI — 21st.dev tarzı parallax
   Üç sıra kare görsel. Kaydırdıkça sıralar zıt yönlerde
   kayıyor ve baştaki 3B eğim düzleşiyor: duvar açılıyor.
   Ortadaki sıra büyük ve net (ön plan), üst/alt sıralar
   küçük ve hafif soluk (arka plan) — biri önde, ötekiler
   arkada kayıyor.
   ══════════════════════════════════════════════════════ */

const IMGS = Array.from({ length: 26 }, (_, k) => `/assets/gallery/g${String(k + 1).padStart(2, '0')}.webp`);
const ROW_TOP = IMGS.filter((_, k) => k % 3 === 0); // 9
const ROW_MID = IMGS.filter((_, k) => k % 3 === 1); // 9  (ön plan)
const ROW_BOT = IMGS.filter((_, k) => k % 3 === 2); // 8

/* Kısa tutuldu: bölüm az kaydırmayla geçiliyor, uzun süre
   sabit kalıp bekletmiyor. */
const UNITS = 1.35;
const UNIT_VH = 100;

export default function GalleryParallax() {
  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    const stage = q('.g-stage')[0];

    if (prefersReduced()) {
      gsap.set(stage, { rotateX: 0, y: 0, scale: 1, opacity: 1 });
      gsap.set(q('.g-row'), { x: 0 });
      return;
    }

    gsap.set(stage, { transformPerspective: 1100, transformOrigin: '50% 30%' });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.35,
        markers: DEBUG,
      },
    });

    /* açılış: eğik ve soluk başlıyor, ilk üçte bir içinde düzleşiyor */
    tl.fromTo(stage,
      { rotateX: 20, rotateZ: 1.2, y: 70, scale: 0.9, opacity: 0.35 },
      { rotateX: 0, rotateZ: 0, y: 0, scale: 1, opacity: 1, ease: 'power2.out', duration: 0.34 }, 0);

    /* yatay parallax: her sıra farklı yön ve hızda, tüm kaydırma boyunca */
    tl.fromTo(q('.g-top'), { xPercent: -3 }, { xPercent: -13, ease: 'none', duration: 1 }, 0);
    tl.fromTo(q('.g-mid'), { xPercent: 4 }, { xPercent: 14, ease: 'none', duration: 1 }, 0);
    tl.fromTo(q('.g-bot'), { xPercent: -4 }, { xPercent: -15, ease: 'none', duration: 1 }, 0);
  }, []);

  const Card = ({ src, big }) => (
    <div
      className={`g-card shrink-0 rounded-2xl overflow-hidden border border-white/10 ${big ? '' : 'opacity-80'}`}
      style={{
        width: big ? 'clamp(190px,21vw,300px)' : 'clamp(128px,14.5vw,208px)',
        aspectRatio: '1 / 1',
        boxShadow: big ? '0 24px 60px rgba(0,0,0,.55)' : '0 12px 34px rgba(0,0,0,.4)',
      }}
    >
      <img src={src} alt="" loading="lazy" decoding="async"
        className="w-full h-full object-cover"
        style={big ? undefined : { filter: 'brightness(.82) saturate(.95)' }} />
    </div>
  );

  return (
    <section
      ref={ref}
      id="galeri"
      className="relative"
      style={{ height: `${UNITS * UNIT_VH}vh`, background: '#050605' }}
    >
      <div className="g-stage sticky top-0 h-screen overflow-hidden flex flex-col justify-center gap-4 md:gap-6"
        style={{ background: '#050605' }}>

        <div className="g-row g-top flex gap-4 md:gap-6 w-max mx-auto will-change-transform">
          {ROW_TOP.map((s) => <Card key={s} src={s} big={false} />)}
        </div>

        <div className="g-row g-mid flex gap-4 md:gap-6 w-max mx-auto will-change-transform">
          {ROW_MID.map((s) => <Card key={s} src={s} big />)}
        </div>

        <div className="g-row g-bot flex gap-4 md:gap-6 w-max mx-auto will-change-transform">
          {ROW_BOT.map((s) => <Card key={s} src={s} big={false} />)}
        </div>

        {/* kenar karartmaları: sıralar kenarlarda yumuşak kayboluyor */}
        <div className="absolute inset-y-0 left-0 w-[14vw] pointer-events-none" aria-hidden="true"
          style={{ background: 'linear-gradient(90deg,#050605 0%,rgba(5,6,5,0) 100%)' }} />
        <div className="absolute inset-y-0 right-0 w-[14vw] pointer-events-none" aria-hidden="true"
          style={{ background: 'linear-gradient(270deg,#050605 0%,rgba(5,6,5,0) 100%)' }} />
        <div className="absolute inset-x-0 top-0 h-[12vh] pointer-events-none" aria-hidden="true"
          style={{ background: 'linear-gradient(180deg,#050605 0%,rgba(5,6,5,0) 100%)' }} />
        <div className="absolute inset-x-0 bottom-0 h-[12vh] pointer-events-none" aria-hidden="true"
          style={{ background: 'linear-gradient(0deg,#050605 0%,rgba(5,6,5,0) 100%)' }} />
      </div>
    </section>
  );
}
